/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';

import api from 'services/api';
import CentralIaProviderSelector, {
  providers,
  getDefaultVersion,
} from 'components/molecules/central_ia_provider_selector';
import { getExpertPhoto } from 'components/organisms/central_ia_library/expert_photos';

import { Overlay } from './styles';

interface Topic {
  slug: string;
  name: string;
}

interface PromptDetail {
  slug: string;
  title: string;
  summary: string;
  body: string;
  topics: Topic[];
  expertSlug: string;
  expertName: string;
  expertHandle: string;
  expertRole: string;
  expertPhoto: string;
}

interface CentralIaPromptModalProps {
  slug: string;
  onClose: () => void;
  onUse: (body: string, providerId: string, model: string) => void;
  showProviderConfig?: boolean;
}

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const CentralIaPromptModal: React.FC<CentralIaPromptModalProps> = ({
  slug,
  onClose,
  onUse,
  showProviderConfig = true,
}) => {
  const [prompt, setPrompt] = useState<PromptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState<'detail' | 'config'>('detail');
  const [chosenProvider, setChosenProvider] = useState(providers[0].id);
  const [chosenVersion, setChosenVersion] = useState(getDefaultVersion(providers[0].id));

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    api
      .get(`/chatbots/central_ia/biblioteca/prompts/${slug}`)
      .then((response) => {
        if (!active) return;
        const data = response.data || {};
        const expert = data.expert || {};
        setPrompt({
          slug: data.slug,
          title: data.title,
          summary: data.summary ?? '',
          body: data.body ?? '',
          topics: Array.isArray(data.topics)
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              data.topics.map((topic: any) => ({ slug: topic.slug, name: topic.name }))
            : [],
          expertSlug: expert.slug ?? '',
          expertName: expert.name ?? '',
          expertHandle: expert.instagram_handle ?? '',
          expertRole: expert.role ?? '',
          expertPhoto: expert.photo_url ?? '',
        });
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!copied) return undefined;
    const timeoutId = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleCopy = async () => {
    if (!prompt?.body) return;
    try {
      await navigator.clipboard.writeText(prompt.body);
      setCopied(true);
    } catch {
      // Clipboard indisponivel — ignora.
    }
  };

  const handleChooseProvider = (providerId: string) => {
    setChosenProvider(providerId);
    setChosenVersion(getDefaultVersion(providerId));
  };

  const handleStart = () => {
    if (!prompt) return;
    onUse(prompt.body, chosenProvider, chosenVersion);
  };

  const photo = prompt ? getExpertPhoto(prompt.expertPhoto) : '';
  const handle = prompt?.expertHandle
    ? `@${prompt.expertHandle.replace(/^@/, '')}`
    : prompt?.expertRole ?? '';
  const chosenProviderObj = providers.find((provider) => provider.id === chosenProvider);

  return (
    <Overlay>
      <div className="pm-overlay">
        <button type="button" className="pm-backdrop" aria-label="Fechar" onClick={onClose} />
        <div className="pm-card" role="dialog" aria-modal="true">
          <button type="button" className="close-button" onClick={onClose} aria-label="Fechar">
            <i className="material-icons">close</i>
          </button>

          <div className="pm-body">
            {loading ? (
              <p className="prompt-modal-title">Carregando...</p>
            ) : error || !prompt ? (
              <p className="prompt-modal-title">Não foi possível carregar o prompt.</p>
            ) : step === 'config' ? (
              <>
                <span className="config-eyebrow d-block text-uppercase">
                  Iniciar chat com prompt
                </span>
                <h5 className="prompt-modal-title">{prompt.title}</h5>
                {prompt.summary && <p className="config-summary">{prompt.summary}</p>}

                <div className="config-label">Escolha a IA:</div>
                <CentralIaProviderSelector
                  selectedProvider={chosenProvider}
                  onSelectProvider={handleChooseProvider}
                />

                <div className="config-label mt-3">Versão</div>
                <select
                  className="form-control"
                  value={chosenVersion}
                  onChange={(event) => setChosenVersion(event.target.value)}
                >
                  {chosenProviderObj?.versions.map((version) => (
                    <option key={version.id} value={version.id}>
                      {version.label}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                {prompt.expertName && (
                  <div className="prompt-expert">
                    {photo ? (
                      <img className="expert-avatar" src={photo} alt={prompt.expertName} />
                    ) : (
                      <div className="expert-avatar">{getInitials(prompt.expertName)}</div>
                    )}
                    <div>
                      <div className="expert-name">{prompt.expertName}</div>
                      {handle && <div className="expert-handle">{handle}</div>}
                    </div>
                  </div>
                )}

                <h5 className="prompt-modal-title">{prompt.title}</h5>

                {prompt.topics.length > 0 && (
                  <div className="prompt-modal-topics">
                    {prompt.topics.map((topic) => (
                      <span key={topic.slug} className="topic-chip">
                        {topic.name}
                      </span>
                    ))}
                  </div>
                )}

                <div className="prompt-modal-body">{prompt.body}</div>
              </>
            )}
          </div>

          <div className="pm-footer">
            {step === 'config' ? (
              <>
                <button type="button" className="copy-button" onClick={() => setStep('detail')}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="use-button"
                  onClick={handleStart}
                  disabled={loading || !prompt?.body}
                >
                  <i className="material-icons">auto_awesome</i>
                  Iniciar chat
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="copy-button"
                  onClick={handleCopy}
                  disabled={loading || !prompt?.body}
                >
                  <i className="material-icons">{copied ? 'check' : 'content_copy'}</i>
                  {copied ? 'Copiado!' : 'Copiar texto'}
                </button>
                <button
                  type="button"
                  className="use-button"
                  onClick={() => (showProviderConfig ? setStep('config') : handleStart())}
                  disabled={loading || !prompt?.body}
                >
                  <i className="material-icons">auto_awesome</i>
                  Usar no chat
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Overlay>
  );
};

export default CentralIaPromptModal;
